import { useState, type JSX } from "react";

/**
 * Scenario.worldview の known fields。他の field (timeline / organizations / concepts 等)
 * が追加された場合も optional としてそのまま受け入れて壊れない設計。
 */
export type WorldviewLike = {
  setting?: unknown;
  tone?: unknown;
  premise?: unknown;
  [key: string]: unknown;
};

export type WorldInfoPanelProps = {
  /** Scenario.worldview (unknown 型の JSON payload、型安全に parse する)。 */
  worldview?: WorldviewLike | null;
  /** 初期状態で開いているか。default は false (折りたたまれた状態)。 */
  defaultOpen?: boolean;
};

const KNOWN_LABELS: Record<string, string> = {
  setting: "舞台",
  tone: "トーン",
  premise: "前提",
  timeline: "時系列",
  organizations: "組織",
  concepts: "主要概念",
};

/**
 * worldview の 1 field を「ラベル: 値」形式で描画するヘルパ。
 * string は そのまま、array は箇条書き、object は JSON 文字列化、他は無視。
 */
function renderField(
  key: string,
  value: unknown,
): JSX.Element | null {
  const label = KNOWN_LABELS[key] ?? key;

  if (typeof value === "string" && value.length > 0) {
    return (
      <div key={key} data-testid={`worldinfo-field-${key}`} className="mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
        <p className="text-sm text-slate-200 leading-snug whitespace-pre-wrap">{value}</p>
      </div>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return (
      <div key={key} data-testid={`worldinfo-field-${key}`} className="mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
        <ul className="text-sm text-slate-200 list-disc list-inside">
          {value.map((v, i) => (
            <li key={`${key}-${i}`}>{typeof v === "string" ? v : JSON.stringify(v)}</li>
          ))}
        </ul>
      </div>
    );
  }
  if (typeof value === "object" && value !== null) {
    if (Object.keys(value).length === 0) return null;
    return (
      <div key={key} data-testid={`worldinfo-field-${key}`} className="mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
        <pre className="text-[11px] text-slate-300 bg-slate-800/40 rounded p-1 overflow-x-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      </div>
    );
  }
  return null;
}

/**
 * worldline-palimpsest (Phase 3-1, Task 5.3) 世界情報パネル。
 *
 * scenario.worldview を折りたたみ accordion で表示する。既知 field (setting/tone/premise)
 * は日本語ラベルで表示、未知 field (future: timeline/organizations/concepts 等) は
 * optional に受け入れて生のまま表示する (Req 9.3 / 9.4 互換性維持)。
 *
 * worldview が undefined / null / 空オブジェクト時もクラッシュせず「情報なし」で描画継続。
 */
export function WorldInfoPanel({
  worldview,
  defaultOpen = false,
}: WorldInfoPanelProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const entries = worldview !== null && worldview !== undefined
    ? Object.entries(worldview)
    : [];
  const hasContent = entries.some(([, v]) => {
    if (typeof v === "string") return v.length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object" && v !== null) return Object.keys(v).length > 0;
    return false;
  });

  return (
    <section
      data-testid="palimpsest-world-info-panel"
      data-open={open ? "true" : "false"}
      className="rounded border border-slate-700/60 bg-slate-900/40"
    >
      <button
        type="button"
        data-testid="palimpsest-world-info-toggle"
        aria-expanded={open}
        aria-controls="palimpsest-world-info-body"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-slate-800/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
      >
        <span>世界情報</span>
        <span aria-hidden="true" className="text-slate-500">
          {open ? "▾" : "▸"}
        </span>
      </button>

      {open && (
        <div
          id="palimpsest-world-info-body"
          data-testid="palimpsest-world-info-body"
          className="px-3 py-2 border-t border-slate-700/60"
        >
          {!hasContent && (
            <p className="text-xs text-slate-500">世界情報はまだ登録されていません。</p>
          )}
          {hasContent &&
            entries
              .map(([k, v]) => renderField(k, v))
              .filter((el): el is JSX.Element => el !== null)}
        </div>
      )}
    </section>
  );
}
